import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  LinearProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  CheckCircle,
  Warning,
  AttachMoney,
  Inventory,
  Assessment,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { api } from '../config/API_URL';
import { motion } from 'framer-motion';

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      const res = await api.get(`/metrc/analytics/overview?range=${timeRange}`);
      setAnalytics(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setLoading(false);
    }
  };

  if (loading || !analytics) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '80vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper 
          elevation={0}
          sx={{ 
            p: 6, 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a202c' }}>
            Loading analytics...
          </Typography>
        </Paper>
      </Box>
    );
  }

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b'];
  const gradientColors = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={8}
          sx={{
            p: 2.5,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#1a202c' }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color, fontWeight: 600 }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box 
      sx={{ 
        background: 'linear-gradient(to bottom, #f7fafc 0%, #edf2f7 100%)',
        minHeight: '100vh',
        pb: 6,
      }}
    >
      {/* Header with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          pt: 4,
          pb: 8,
          mb: -4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4,
          },
        }}
      >
        <Box sx={{ maxWidth: 1600, mx: 'auto', px: 4, position: 'relative', zIndex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 800, 
                  color: 'white',
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                Analytics Overview
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 400,
                }}
              >
                Real-time insights and performance metrics
              </Typography>
            </Box>
            <FormControl 
              sx={{ 
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 600,
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  '&.Mui-focused': {
                    color: 'white',
                  },
                },
                '& .MuiSelect-icon': {
                  color: 'white',
                },
              }}
            >
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                label="Time Range"
              >
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="1y">Last Year</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1600, mx: 'auto', px: 4 }}>
        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: 'Compliance Score',
              value: `${analytics.compliance_score}%`,
              icon: CheckCircle,
              gradient: gradientColors[0],
              showProgress: true,
              progressValue: analytics.compliance_score,
            },
            {
              title: 'Total Revenue',
              value: `$${analytics.total_revenue?.toLocaleString()}`,
              icon: AttachMoney,
              gradient: gradientColors[1],
              change: analytics.revenue_change,
              changePositive: true,
            },
            {
              title: 'Inventory Value',
              value: `$${analytics.inventory_value?.toLocaleString()}`,
              icon: Inventory,
              gradient: gradientColors[2],
              subtitle: `${analytics.package_count} active packages`,
            },
            {
              title: 'Avg Daily Sales',
              value: `$${analytics.avg_daily_sales?.toLocaleString()}`,
              icon: TrendingUp,
              gradient: gradientColors[3],
              subtitle: `${analytics.transactions_count} transactions`,
            },
          ].map((kpi, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'white',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: kpi.gradient,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              mb: 1.5,
                            }}
                          >
                            {kpi.title}
                          </Typography>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 800,
                              color: '#1a202c',
                              mb: 1,
                            }}
                          >
                            {kpi.value}
                          </Typography>
                          {kpi.change && (
                            <Chip
                              icon={kpi.changePositive ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />}
                              label={`${kpi.changePositive ? '+' : ''}${kpi.change}%`}
                              size="small"
                              sx={{
                                background: kpi.changePositive ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                                color: kpi.changePositive ? '#10b981' : '#ef4444',
                                fontWeight: 700,
                                border: '1px solid',
                                borderColor: kpi.changePositive ? alpha('#10b981', 0.2) : alpha('#ef4444', 0.2),
                              }}
                            />
                          )}
                          {kpi.subtitle && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                              {kpi.subtitle}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2,
                            background: kpi.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          }}
                        >
                          <kpi.icon sx={{ fontSize: 32, color: 'white' }} />
                        </Box>
                      </Stack>
                      {kpi.showProgress && (
                        <LinearProgress
                          variant="determinate"
                          value={kpi.progressValue}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            background: alpha('#667eea', 0.1),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: kpi.gradient,
                            },
                          }}
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Compliance Trend - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card 
            elevation={0}
            sx={{ 
              mb: 4, 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: 3,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                📈 Compliance Trend
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                Track your compliance performance over time
              </Typography>
            </Box>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <LineChart 
                    data={analytics.compliance_trend}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <defs>
                      <linearGradient id="colorCompliance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#667eea" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#667eea" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      domain={[0, 100]}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                      label={{ 
                        value: 'Completion Rate (%)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { fontSize: 14, fill: '#64748b', fontWeight: 600 }
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="completion_rate"
                      stroke="#667eea"
                      strokeWidth={3}
                      name="Completion Rate"
                      dot={{ fill: '#667eea', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7, strokeWidth: 0 }}
                      fillOpacity={1}
                      fill="url(#colorCompliance)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Revenue and Tasks Row */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={7}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Card 
                elevation={0}
                sx={{ 
                  height: 550,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    p: 3,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                    💰 Revenue Trend
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                    Daily revenue performance analysis
                  </Typography>
                </Box>
                <CardContent sx={{ p: 4, height: 'calc(100% - 88px)' }}>
                  <Box sx={{ width: '100%', height: '100%' }}>
                    <ResponsiveContainer>
                      <BarChart 
                        data={analytics.revenue_trend}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f093fb" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#f5576c" stopOpacity={0.8}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          tickLine={false}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={false}
                          label={{ 
                            value: 'Revenue ($)', 
                            angle: -90, 
                            position: 'insideLeft',
                            style: { fontSize: 14, fill: '#64748b', fontWeight: 600 }
                          }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey="revenue" 
                          fill="url(#colorRevenue)"
                          name="Daily Revenue" 
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} lg={5}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <Card 
                elevation={0}
                sx={{ 
                  height: 550,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    p: 3,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                    📊 Tasks by Category
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                    Distribution of task categories
                  </Typography>
                </Box>
                <CardContent sx={{ p: 4, height: 'calc(100% - 88px)' }}>
                  <Box sx={{ width: '100%', height: '100%' }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <defs>
                          {COLORS.map((color, index) => (
                            <linearGradient key={index} id={`pieGradient${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={color} stopOpacity={0.9}/>
                              <stop offset="95%" stopColor={color} stopOpacity={0.7}/>
                            </linearGradient>
                          ))}
                        </defs>
                        <Pie
                          data={analytics.task_breakdown}
                          cx="50%"
                          cy="45%"
                          labelLine={false}
                          label={(entry) => entry.value}
                          outerRadius="60%"
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {analytics.task_breakdown?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={`url(#pieGradient${index % COLORS.length})`} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                          verticalAlign="bottom" 
                          height={80}
                          formatter={(value, entry) => entry.payload.name}
                          wrapperStyle={{ fontSize: 14, fontWeight: 500 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Recent Alerts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                p: 3,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                🔔 Recent Alerts
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                Important notifications and warnings
              </Typography>
            </Box>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={2.5}>
                {analytics.recent_alerts?.map((alert, idx) => (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: alert.severity === 'high' ? alpha('#ef4444', 0.3) : alpha('#f59e0b', 0.3),
                      background: alert.severity === 'high' 
                        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%)'
                        : 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.02) 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1.5,
                          background: alert.severity === 'high' 
                            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                            : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Warning sx={{ fontSize: 28, color: 'white' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#1a202c' }}>
                          {alert.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.6 }}>
                          {alert.message}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {new Date(alert.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
                {(!analytics.recent_alerts || analytics.recent_alerts.length === 0) && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 6,
                      textAlign: 'center',
                      background: alpha('#10b981', 0.05),
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: alpha('#10b981', 0.2),
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 64, color: alpha('#10b981', 0.5), mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      All Clear! No Recent Alerts
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      Your system is running smoothly
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      </Box>
    </Box>
  );
}
